import type { BusinessOnboardingState } from '../../../types/dashboard'

// ─── Owner steps ─────────────────────────────────────────────────────────────

export type OwnerOnboardingStepId =
  | 'iacrm'
  | 'avatar'
  | 'identity'
  | 'pack'
  | 'program'
  | 'agent'
  | 'assign'
  | 'prospect'

export type AgentOnboardingStepId = 'agent-avatar' | 'programs' | 'prospect' | 'converted'

export type OnboardingStepId = OwnerOnboardingStepId | AgentOnboardingStepId

export interface AgentOnboardingState {
  avatar_set: boolean
  programs_count: number
  prospects_count: number
  converted_prospects_count: number
}

export interface OnboardingStep {
  id: OnboardingStepId
  i18nKey: string
  route: string
  targetAttr: string | null
  isComplete: (state: BusinessOnboardingState | AgentOnboardingState) => boolean
}

export const OWNER_ONBOARDING_STEPS: (Omit<OnboardingStep, 'isComplete'> & {
  isComplete: (state: BusinessOnboardingState) => boolean
})[] = [
  {
    id: 'iacrm',
    i18nKey: 'onboarding.steps.iacrm',
    route: '/settings?tab=api&onboard=iacrm',
    targetAttr: 'iacrm',
    isComplete: (s) => s.iacrm_configured,
  },
  {
    id: 'avatar',
    i18nKey: 'onboarding.steps.avatar',
    route: '/settings?onboard=avatar',
    targetAttr: 'avatar',
    isComplete: (s) => s.avatar_set,
  },
  {
    id: 'identity',
    i18nKey: 'onboarding.steps.identity',
    route: '/settings?onboard=identity',
    targetAttr: 'identity',
    isComplete: (s) => s.business_identity_set,
  },
  {
    id: 'pack',
    i18nKey: 'onboarding.steps.pack',
    route: '/exchange-packs?onboard=pack',
    targetAttr: 'pack',
    isComplete: (s) => s.packs_count > 0,
  },
  {
    id: 'program',
    i18nKey: 'onboarding.steps.program',
    route: '/programs?onboard=program',
    targetAttr: 'program',
    isComplete: (s) => s.programs_count > 0,
  },
  {
    id: 'agent',
    i18nKey: 'onboarding.steps.agent',
    route: '/agents?onboard=agent',
    targetAttr: 'agent',
    isComplete: (s) => s.agents_count > 0,
  },
  {
    id: 'assign',
    i18nKey: 'onboarding.steps.assign',
    route: '/programs?onboard=assign',
    targetAttr: null,
    isComplete: (s) => s.agents_with_assignment_count > 0,
  },
  {
    id: 'prospect',
    i18nKey: 'onboarding.steps.prospect',
    route: '/prospects',
    targetAttr: null,
    isComplete: (s) => s.prospects_count > 0,
  },
]

export const AGENT_ONBOARDING_STEPS: (Omit<OnboardingStep, 'isComplete'> & {
  isComplete: (state: AgentOnboardingState) => boolean
})[] = [
  {
    id: 'agent-avatar',
    i18nKey: 'onboarding.agentSteps.avatar',
    route: '/settings?onboard=agent-avatar',
    targetAttr: 'avatar',
    isComplete: (s) => s.avatar_set,
  },
  {
    id: 'programs',
    i18nKey: 'onboarding.agentSteps.programs',
    route: '/programs',
    targetAttr: null,
    isComplete: (s) => s.programs_count > 0,
  },
  {
    id: 'prospect',
    i18nKey: 'onboarding.agentSteps.prospect',
    route: '/prospects?onboard=prospect',
    targetAttr: 'prospect',
    isComplete: (s) => s.prospects_count > 0,
  },
  {
    id: 'converted',
    i18nKey: 'onboarding.agentSteps.converted',
    route: '/prospects',
    targetAttr: null,
    isComplete: (s) => s.converted_prospects_count > 0,
  },
]
